import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Globe,
  Shield,
  ShieldCheck,
  RefreshCw,
  Server,
  FileCode,
  Save,
  ExternalLink,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  MapPin,
  Plus,
  Trash2,
  Edit2,
  Upload,
} from 'lucide-react';
import { domainsApi, nginxApi, Domain, SSLCertificate, NginxConfig, DomainLocation, DomainLocationCreate, DomainLocationUpdate } from '../../lib/api/domains';
import { serversApi } from '../../lib/api/servers';
import { appsApi, App } from '../../lib/api/apps';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { toast } from '../../stores/toastStore';
import { ConfirmModal } from '../../components/ui/ConfirmModal';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-700',
  error: 'bg-red-100 text-red-700',
  expired: 'bg-red-100 text-red-700',
};

const statusIcons = {
  pending: Clock,
  active: CheckCircle,
  inactive: AlertCircle,
  error: AlertCircle,
  expired: AlertCircle,
};

export function DomainDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showNginxEditor, setShowNginxEditor] = useState(false);
  const [nginxContent, setNginxContent] = useState('');
  const [issuingSSL, setIssuingSSL] = useState(false);
  const [deployingNginx, setDeployingNginx] = useState(false);

  // Location modal state
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<DomainLocation | null>(null);
  const [locationForm, setLocationForm] = useState({
    app_id: '',
    path: '/',
    upstream_port: 8000,
    upstream_host: 'localhost',
    priority: 0,
    is_active: true,
  });
  const [deleteLocationId, setDeleteLocationId] = useState<string | null>(null);

  const { data: domain, isLoading } = useQuery({
    queryKey: ['domain', id],
    queryFn: () => domainsApi.get(id!),
  });

  const { data: server } = useQuery({
    queryKey: ['server', domain?.server_id],
    queryFn: () => serversApi.get(domain!.server_id),
    enabled: !!domain?.server_id,
  });

  const { data: sslCert, refetch: refetchSSL } = useQuery({
    queryKey: ['domain-ssl', id],
    queryFn: () => domainsApi.getSSL(id!).catch(() => null),
    enabled: !!id,
  });

  const { data: nginxConfigs } = useQuery({
    queryKey: ['nginx-configs', domain?.server_id],
    queryFn: () => nginxApi.list({ server_id: domain!.server_id }),
    enabled: !!domain?.server_id,
  });

  const { data: locations, refetch: refetchLocations } = useQuery({
    queryKey: ['domain-locations', id],
    queryFn: () => domainsApi.listLocations(id!),
    enabled: !!id,
  });

  const { data: apps } = useQuery({
    queryKey: ['apps', domain?.server_id],
    queryFn: () => appsApi.list({ server_id: domain?.server_id, limit: 100 }),
    enabled: !!domain?.server_id,
  });

  // Find nginx config for this domain
  const domainNginxConfig = nginxConfigs?.items.find(
    (c) => c.domain_id === id || c.name === domain?.name
  );

  const issueSSL = useMutation({
    mutationFn: () => domainsApi.issueSSL(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domain-ssl', id] });
      queryClient.invalidateQueries({ queryKey: ['domain', id] });
      toast.success('SSL certificate issued successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to issue SSL certificate');
    },
  });

  const deployNginxMutation = useMutation({
    mutationFn: () => domainsApi.deployNginx(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nginx-configs'] });
      toast.success('Nginx config deployed successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to deploy nginx config');
    },
  });

  const deployOldNginx = useMutation({
    mutationFn: (configId: string) => nginxApi.deploy(configId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nginx-configs'] });
      toast.success('Nginx config deployed successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to deploy nginx config');
    },
  });

  const updateNginx = useMutation({
    mutationFn: ({ configId, content }: { configId: string; content: string }) =>
      nginxApi.update(configId, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nginx-configs'] });
      toast.success('Nginx config updated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update nginx config');
    },
  });

  const addLocation = useMutation({
    mutationFn: (data: DomainLocationCreate) => domainsApi.addLocation(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domain-locations', id] });
      toast.success('Location added successfully');
      closeLocationModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to add location');
    },
  });

  const updateLocation = useMutation({
    mutationFn: ({ locationId, data }: { locationId: string; data: DomainLocationUpdate }) =>
      domainsApi.updateLocation(id!, locationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domain-locations', id] });
      toast.success('Location updated successfully');
      closeLocationModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update location');
    },
  });

  const deleteLocation = useMutation({
    mutationFn: (locationId: string) => domainsApi.removeLocation(id!, locationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domain-locations', id] });
      toast.success('Location removed successfully');
      setDeleteLocationId(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to remove location');
    },
  });

  const handleIssueSSL = async () => {
    setIssuingSSL(true);
    try {
      await issueSSL.mutateAsync();
    } finally {
      setIssuingSSL(false);
    }
  };

  const handleDeployNginx = async () => {
    setDeployingNginx(true);
    try {
      await deployNginxMutation.mutateAsync();
    } finally {
      setDeployingNginx(false);
    }
  };

  const handleSaveNginx = async () => {
    if (!domainNginxConfig) return;
    await updateNginx.mutateAsync({
      configId: domainNginxConfig.id,
      content: nginxContent,
    });
    setShowNginxEditor(false);
  };

  const openNginxEditor = () => {
    if (domainNginxConfig) {
      setNginxContent(domainNginxConfig.content);
      setShowNginxEditor(true);
    }
  };

  const openAddLocationModal = () => {
    setEditingLocation(null);
    setLocationForm({
      app_id: apps?.items?.[0]?.id || '',
      path: '/',
      upstream_port: 8000,
      upstream_host: 'localhost',
      priority: 0,
      is_active: true,
    });
    setShowLocationModal(true);
  };

  const openEditLocationModal = (location: DomainLocation) => {
    setEditingLocation(location);
    setLocationForm({
      app_id: location.app_id,
      path: location.path,
      upstream_port: location.upstream_port,
      upstream_host: location.upstream_host,
      priority: location.priority,
      is_active: location.is_active,
    });
    setShowLocationModal(true);
  };

  const closeLocationModal = () => {
    setShowLocationModal(false);
    setEditingLocation(null);
  };

  const handleSaveLocation = () => {
    if (editingLocation) {
      updateLocation.mutate({
        locationId: editingLocation.id,
        data: {
          path: locationForm.path,
          upstream_port: locationForm.upstream_port,
          upstream_host: locationForm.upstream_host,
          priority: locationForm.priority,
          is_active: locationForm.is_active,
        },
      });
    } else {
      addLocation.mutate({
        app_id: locationForm.app_id,
        path: locationForm.path,
        upstream_port: locationForm.upstream_port,
        upstream_host: locationForm.upstream_host,
        priority: locationForm.priority,
        is_active: locationForm.is_active,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (!domain) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <p className="text-red-600">Domain not found</p>
        <Button variant="link" onClick={() => navigate('/domains')}>
          Back to Domains
        </Button>
      </div>
    );
  }

  const StatusIcon = statusIcons[domain.status];
  const appsList = apps?.items || [];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/domains')}
        className="gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Domains
      </Button>

      {/* Domain Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-primary-100">
                <Globe className="h-8 w-8 text-primary-600" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900">{domain.name}</h1>
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded-full flex items-center gap-1 ${statusColors[domain.status]}`}
                  >
                    <StatusIcon className="h-3 w-3" />
                    {domain.status}
                  </span>
                </div>
                {server && (
                  <p className="text-gray-500 flex items-center gap-1 mt-1">
                    <Server className="h-4 w-4" />
                    {server.name} ({server.host})
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {domain.ssl_enabled ? (
                <div className="flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1 rounded-full">
                  <ShieldCheck className="h-4 w-4" />
                  <span className="text-sm font-medium">SSL Enabled</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  <Shield className="h-4 w-4" />
                  <span className="text-sm font-medium">No SSL</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Locations Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Locations ({locations?.length || 0})
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeployNginx}
                disabled={deployingNginx || !locations?.length}
                className="gap-1"
              >
                {deployingNginx ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                Deploy Nginx
              </Button>
              <Button size="sm" onClick={openAddLocationModal} className="gap-1">
                <Plus className="h-4 w-4" />
                Add Location
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {locations && locations.length > 0 ? (
            <div className="space-y-3">
              {locations.map((location) => (
                <div
                  key={location.id}
                  className={`p-4 border rounded-lg ${
                    location.is_active ? 'bg-white' : 'bg-gray-50 border-dashed'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-semibold bg-gray-100 px-2 py-0.5 rounded">
                          {location.path}
                        </code>
                        <span className="text-gray-400">→</span>
                        <span className="text-sm text-gray-600">
                          {location.upstream_host}:{location.upstream_port}
                        </span>
                        {!location.is_active && (
                          <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                            Inactive
                          </span>
                        )}
                      </div>
                      {location.app_name && (
                        <Link
                          to={`/apps/${location.app_id}`}
                          className="text-sm text-primary-600 hover:text-primary-700 mt-1 inline-flex items-center gap-1"
                        >
                          {location.app_name}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      )}
                      <p className="text-xs text-gray-400 mt-1">Priority: {location.priority}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditLocationModal(location)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteLocationId(location.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <MapPin className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 mb-4">No locations configured</p>
              <p className="text-sm text-gray-400 mb-4">
                Add locations to route different paths to different apps
              </p>
              <Button onClick={openAddLocationModal} className="gap-1">
                <Plus className="h-4 w-4" />
                Add Location
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* SSL Certificate Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            SSL Certificate
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sslCert ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[sslCert.status]}`}
                  >
                    {sslCert.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Issuer</p>
                  <p className="font-medium">{sslCert.issuer || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Issued At</p>
                  <p className="font-medium">
                    {sslCert.issued_at
                      ? new Date(sslCert.issued_at).toLocaleDateString()
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Expires At</p>
                  <p className="font-medium">
                    {sslCert.expires_at
                      ? new Date(sslCert.expires_at).toLocaleDateString()
                      : '-'}
                  </p>
                </div>
              </div>
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-500 mb-2">Certificate Paths</p>
                <code className="block text-xs bg-gray-100 p-2 rounded">
                  {sslCert.cert_path || 'Not configured'}
                </code>
                <code className="block text-xs bg-gray-100 p-2 rounded mt-1">
                  {sslCert.key_path || 'Not configured'}
                </code>
              </div>
              <Button
                variant="outline"
                onClick={handleIssueSSL}
                disabled={issuingSSL}
                className="gap-2"
              >
                {issuingSSL ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Renew Certificate
              </Button>
            </div>
          ) : (
            <div className="text-center py-6">
              <Shield className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 mb-4">No SSL certificate configured</p>
              <Button onClick={handleIssueSSL} disabled={issuingSSL} className="gap-2">
                {issuingSSL ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ShieldCheck className="h-4 w-4" />
                )}
                Issue SSL Certificate
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Nginx Configuration Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileCode className="h-5 w-5" />
              Nginx Configuration
            </CardTitle>
            {domainNginxConfig && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openNginxEditor}
                  className="gap-1"
                >
                  <FileCode className="h-4 w-4" />
                  Edit Config
                </Button>
                <Button
                  size="sm"
                  onClick={() => deployOldNginx.mutate(domainNginxConfig.id)}
                  disabled={deployOldNginx.isPending}
                  className="gap-1"
                >
                  {deployOldNginx.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ExternalLink className="h-4 w-4" />
                  )}
                  Deploy
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {domainNginxConfig ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                      domainNginxConfig.is_active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {domainNginxConfig.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Version</p>
                  <p className="font-medium">v{domainNginxConfig.version}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">File Path</p>
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                    {domainNginxConfig.file_path || '/etc/nginx/conf.d/' + domain.name + '.conf'}
                  </code>
                </div>
              </div>

              {showNginxEditor && (
                <div className="space-y-3 pt-4 border-t">
                  <textarea
                    value={nginxContent}
                    onChange={(e) => setNginxContent(e.target.value)}
                    className="w-full h-96 font-mono text-sm p-3 border rounded-lg bg-gray-50"
                    placeholder="Nginx configuration..."
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowNginxEditor(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveNginx}
                      disabled={updateNginx.isPending}
                      className="gap-1"
                    >
                      {updateNginx.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      Save Config
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <FileCode className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No nginx configuration found for this domain</p>
              <p className="text-sm text-gray-400 mt-1">
                Add locations and deploy to generate nginx configuration
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Location Modal */}
      <Modal
        isOpen={showLocationModal}
        onClose={closeLocationModal}
        title={editingLocation ? 'Edit Location' : 'Add Location'}
        size="md"
      >
        <div className="space-y-4">
          {!editingLocation && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                App
              </label>
              <Select
                value={locationForm.app_id}
                onChange={(e) => setLocationForm({ ...locationForm, app_id: e.target.value })}
                options={appsList.map((app: App) => ({ value: app.id, label: app.name }))}
                placeholder="Select an app"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Path
            </label>
            <Input
              value={locationForm.path}
              onChange={(e) => setLocationForm({ ...locationForm, path: e.target.value })}
              placeholder="/"
            />
            <p className="text-xs text-gray-500 mt-1">
              e.g., / for root, /api for API routes
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upstream Host
              </label>
              <Input
                value={locationForm.upstream_host}
                onChange={(e) => setLocationForm({ ...locationForm, upstream_host: e.target.value })}
                placeholder="localhost"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upstream Port
              </label>
              <Input
                type="number"
                value={locationForm.upstream_port}
                onChange={(e) => setLocationForm({ ...locationForm, upstream_port: parseInt(e.target.value) || 8000 })}
                placeholder="8000"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <Input
              type="number"
              value={locationForm.priority}
              onChange={(e) => setLocationForm({ ...locationForm, priority: parseInt(e.target.value) || 0 })}
              placeholder="0"
            />
            <p className="text-xs text-gray-500 mt-1">
              Higher priority locations are matched first
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={locationForm.is_active}
              onChange={(e) => setLocationForm({ ...locationForm, is_active: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">
              Active
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={closeLocationModal}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveLocation}
              disabled={addLocation.isPending || updateLocation.isPending || (!editingLocation && !locationForm.app_id)}
            >
              {(addLocation.isPending || updateLocation.isPending) ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {editingLocation ? 'Save Changes' : 'Add Location'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deleteLocationId}
        onClose={() => setDeleteLocationId(null)}
        onConfirm={() => deleteLocationId && deleteLocation.mutate(deleteLocationId)}
        title="Delete Location"
        message="Are you sure you want to delete this location? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        isLoading={deleteLocation.isPending}
      />
    </div>
  );
}
